import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Scale, Coins, UserCheck } from 'lucide-react';
import './RewardsPage.css';

const STORE_ITEMS = [
  {
    id: 'contempt_clown',
    name: 'Contempt of Court: Clown Badge',
    cost: 150,
    icon: '🤡',
    description: 'Slap an official Clown badge on a friend\'s profile for 7 days.',
    needsTarget: true
  },
  {
    id: 'contempt_dunce',
    name: 'Contempt of Court: Dunce Cap',
    cost: 150,
    icon: '🎓',
    description: 'Assign a Dunce Cap flair to a friend who had a completely unhinged take.',
    needsTarget: true
  },
  {
    id: 'post_bail',
    name: 'Post Bail: Expunge Record',
    cost: 200,
    icon: '⚖️',
    description: 'Clear all active Contempt badges and expunge your judicial record.',
    needsTarget: false
  },
  {
    id: 'subpoena',
    name: 'Judicial Subpoena',
    cost: 100,
    icon: '📜',
    description: 'Officially summon a friend to file a case on the docket regarding a recent incident.',
    needsTarget: true
  },
  {
    id: 'real_coffee_bounty',
    name: 'Iced Coffee Bounty Claim',
    cost: 350,
    icon: '☕',
    description: 'Claim a real-world coffee tribute from the circle\'s lowest-ranked juror.',
    needsTarget: false
  }
];

const RewardsPage = () => {
  const { currentUser, userData, getIdToken, fetchUserData } = useAuth();
  const [friends, setFriends] = useState([]);
  const [selectedTargetUser, setSelectedTargetUser] = useState('');
  const [purchasingId, setPurchasingId] = useState(null);

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const token = await getIdToken();
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const res = await axios.get(`${apiUrl}/api/users/roster`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          // Filter out current user from target selection
          setFriends(res.data.roster.filter(u => u.uid !== currentUser?.uid));
        }
      } catch (err) {
        console.error('Error fetching roster:', err);
      }
    };

    if (currentUser) {
      fetchFriends();
    }
  }, [currentUser, getIdToken]);

  const handlePurchase = async (item) => {
    if ((userData?.points || 0) < item.cost) {
      toast.error(`Insufficient chips! You need ${item.cost} chips, but only have ${userData?.points || 0}.`);
      return;
    }

    if (item.needsTarget && !selectedTargetUser) {
      toast.warn('Select a friend from the roster to target with this order!');
      return;
    }

    try {
      setPurchasingId(item.id);
      const token = await getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const res = await axios.post(
        `${apiUrl}/api/rewards/purchase`,
        {
          itemId: item.id,
          targetUserId: item.needsTarget ? selectedTargetUser : null,
          reason: 'Judicial order issued by peer juror.'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(res.data.message || 'Order executed! Chips deducted.');
      if (currentUser) {
        await fetchUserData(currentUser.uid);
      }
    } catch (err) {
      console.error('Purchase error:', err);
      toast.error(err.response?.data?.error || 'Purchase failed.');
    } finally {
      setPurchasingId(null);
    }
  };

  return (
    <div className="bail-store-page">
      <Navbar />
      <div className="bail-store-container">
        {/* Store Hero */}
        <div className="store-hero">
          <div className="store-hero-icon">
            <Scale size={40} />
          </div>
          <h1>Bail & Contempt Store</h1>
          <p>
            Redeem the chips you earn from voting on the jury, filing cases, and writing the sharpest roasts.
          </p>

          <div className="chip-balance-card">
            <Coins className="coin-icon" size={24} />
            <div className="balance-info">
              <span className="balance-label">Your Court Treasury Balance</span>
              <span className="balance-amount">{userData?.points || 0} Chips</span>
            </div>
            {userData?.contemptBadges && userData.contemptBadges.length > 0 && (
              <div className="active-contempt-tags">
                <span>Active Contempt:</span>
                {userData.contemptBadges.map((b, i) => (
                  <span key={i} className="contempt-badge-pill">{b}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Friend Target Selection */}
        {friends.length > 0 && (
          <div className="target-selector-box">
            <label htmlFor="target-select">
              <UserCheck size={18} />
              Select Juror to Target for Contempt Badges & Subpoenas:
            </label>
            <select
              id="target-select"
              value={selectedTargetUser}
              onChange={(e) => setSelectedTargetUser(e.target.value)}
            >
              <option value="">-- Choose a friend from the Bar --</option>
              {friends.map(f => (
                <option key={f.uid} value={f.uid}>
                  {f.name} ({f.profession || 'Juror'}) {f.contemptBadges?.length ? `• ${f.contemptBadges.join(', ')}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Store Items Grid */}
        <div className="store-catalog-grid">
          {STORE_ITEMS.map(item => {
            const canAfford = (userData?.points || 0) >= item.cost;
            const isTargetMissing = item.needsTarget && !selectedTargetUser;

            return (
              <div key={item.id} className="store-item-card">
                <div className="item-icon-bubble">{item.icon}</div>
                <div className="item-details">
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                </div>
                <div className="item-card-bottom">
                  <div className="item-price">
                    <Coins size={16} />
                    <span>{item.cost} Chips</span>
                  </div>
                  <button
                    className="btn-buy-store"
                    disabled={!canAfford || purchasingId === item.id || isTargetMissing}
                    onClick={() => handlePurchase(item)}
                  >
                    {purchasingId === item.id 
                      ? 'Executing Order...' 
                      : !canAfford 
                        ? 'Insufficient Chips' 
                        : isTargetMissing 
                          ? 'Select Target Above' 
                          : 'Execute Order'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RewardsPage;
